import { FormRenderProps } from '@progress/kendo-react-form';
import { ContentTypeFieldResponse } from '../../../../Model/responseDtos/ContentTypeFieldResponse';
import { GEAR_CONTENT_TYPE_FIELD_TYPE } from '../../../../Model/enums/GearContentTypeFieldType';
import { useFieldState } from './useFieldState';
import { useCurrentUserContext } from '../../../../context/currentUser/CurrentUserContext';
import { useLoadFieldValues } from '../../../../context/fields/useLoadFieldValues';
import { LoadFieldValuesRequest } from '../../../../Model/requests/LoadFieldValuesRequest';
import {
  SEARCHABLE_FIELD_DEFAULT_DEBOUNCE_TIMER,
  SEARCHABLE_FIELD_DEFAULT_PAGE,
  SEARCHABLE_FIELD_DEFAULT_PAGE_SIZE
} from '../../../../config/consts';
import {
  buildCurrentFormValues,
  buildFormValuesWithClearedDependents,
  clearDependentFields,
  fillFormFieldsFromResponse,
  getDependentFields,
  isDateComplete,
  isValueEmpty,
  separateFileMetadata,
  shouldEndDueToFileDependency,
  shouldTriggerFormFiller,
  updateFilesWithPerFileMetadata
} from './FieldInteractionsHelper';
import { LoadFieldValuesFromFileRequest } from '../../../../Model/requests/LoadFieldValuesFromFileRequest';
import { useGetContentType } from '../../../../context/contentTypes/useGetContentType';
import { useLoadFieldValuesFromFile } from '../../../../context/fields/useLoadFieldValuesFromFile';
import { useGlobalizationContext } from '../../../../context/globalization/GlobalizationContext';

interface UseFieldInteractionsProps {
  ctFields: ContentTypeFieldResponse[];
  selectedCtName?: string;
}

interface UseFieldInteractionsReturn {
  getFieldChangeHandler: (field: ContentTypeFieldResponse) => FieldChangeHandler;
  getChoiceFilterHandler: (field: ContentTypeFieldResponse) => FieldChangeHandler;
  getManualSearchHandler: (field: ContentTypeFieldResponse) => FieldChangeHandler;
  loadingFields: Set<string>;
  dynamicChoices: Record<string, string[]>;
  getFieldChoices: (field: ContentTypeFieldResponse) => string[];
  handleFileFieldChange: (fieldName: string, files: FileList, formRenderProps: FormRenderProps) => Promise<void>;
  isFieldEnabled: (field: ContentTypeFieldResponse) => boolean;
}

type FieldChangeHandler = (fieldName: string, value: any, formRenderProps: FormRenderProps) => Promise<void>;

export const useFieldInteractions = ({
  ctFields,
  selectedCtName
}: UseFieldInteractionsProps): UseFieldInteractionsReturn => {
  const fieldState = useFieldState();
  const { selectedIndex } = useCurrentUserContext();
  const { mutateAsync: loadFieldValues } = useLoadFieldValues();
  const { selectedLocale } = useGlobalizationContext();
  const { data: contentType } = useGetContentType(selectedCtName, selectedLocale);
  const { mutateAsync: loadFieldValuesFromFile } = useLoadFieldValuesFromFile();

  const callLoadFieldValuesApi = async (
    fieldName: string,
    currentFormValues: Record<string, any>,
    searchText?: string
  ) => {
    const field = ctFields.find(f => f.name === fieldName);
    if (!field || !selectedCtName || selectedIndex?.id == null) {
      return null;
    }

    fieldState.addLoadingField(fieldName);

    try {
      const request: LoadFieldValuesRequest = {
        contentTypeName: selectedCtName,
        metadata: currentFormValues,
        ...(searchText && {
          searchText,
          page: SEARCHABLE_FIELD_DEFAULT_PAGE,
          pageSize: SEARCHABLE_FIELD_DEFAULT_PAGE_SIZE
        })
      };

      const fieldId = field.id ?? -1;
      return await loadFieldValues({ fieldId, request });
    } finally {
      fieldState.removeLoadingField(fieldName);
    }
  };

  const loadDependentChoiceOptions = async (
    dependentFieldName: string,
    currentFormValues: Record<string, any>,
    formRenderProps: FormRenderProps
  ) => {
    const dependentField = ctFields.find(f => f.name === dependentFieldName);
    if (!dependentField || !dependentField.usesExtDictionary) {
      return;
    }

    const response = await callLoadFieldValuesApi(dependentFieldName, currentFormValues);

    if (response?.values && response.values.length > 0) {
      fieldState.setFieldChoices(dependentFieldName, response.values);
      fieldState.addEnabledField(dependentFieldName);

      if (response.values.length === 1) {
        formRenderProps.onChange(dependentFieldName, { value: response.values[0] });
      }
    } else {
      fieldState.clearFieldChoices(dependentFieldName);
      fieldState.removeEnabledField(dependentFieldName);
    }
  };

  const handleDependencies = async (fieldName: string, value: any, formRenderProps: FormRenderProps) => {
    const dependentFields = getDependentFields(fieldName, ctFields);

    if (dependentFields.length === 0) {
      return;
    }

    clearDependentFields(dependentFields, formRenderProps);

    const currentFormValues = buildFormValuesWithClearedDependents(
      fieldName,
      value,
      formRenderProps,
      ctFields,
      dependentFields
    );

    for (const dependentField of dependentFields) {
      await loadDependentChoiceOptions(dependentField, currentFormValues, formRenderProps);
    }
  };

  const onUndefinedFieldChange = async (
    fieldName: string,
    value: any,
    formRenderProps: FormRenderProps
  ): Promise<void> => {
    formRenderProps.onChange(fieldName, { value });
  };

  const onStringNumberFieldChange = async (
    fieldName: string,
    value: any,
    formRenderProps: FormRenderProps
  ): Promise<void> => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field) {
      return;
    }

    if (shouldEndDueToFileDependency(field)) {
      return;
    }

    await handleDependencies(fieldName, value, formRenderProps);

    const currentFormValues = buildCurrentFormValues(fieldName, value, formRenderProps, ctFields);

    if (field.searchable) {
      const shouldFillForm = shouldTriggerFormFiller(field, value);

      if (shouldFillForm) {
        const response = await callLoadFieldValuesApi(fieldName, currentFormValues);

        fillFormFieldsFromResponse(response, formRenderProps);
        return;
      }

      fieldState.clearSearchTimer(fieldName);

      if (isValueEmpty(value)) {
        fieldState.clearFieldChoices(fieldName);
        return;
      }

      const timer = setTimeout(async () => {
        const response = await callLoadFieldValuesApi(fieldName, currentFormValues, value.toString());

        if (response?.values && response.values.length > 0) {
          fieldState.setFieldChoices(fieldName, response.values);
        } else {
          fieldState.clearFieldChoices(fieldName);
        }

        fieldState.clearSearchTimer(fieldName);
      }, SEARCHABLE_FIELD_DEFAULT_DEBOUNCE_TIMER);

      fieldState.setSearchTimer(fieldName, timer);
    } else {
      if (shouldTriggerFormFiller(field, value)) {
        const response = await callLoadFieldValuesApi(fieldName, currentFormValues);
        fillFormFieldsFromResponse(response, formRenderProps);
      }
    }
  };

  const onDateFieldChange = async (fieldName: string, value: any, formRenderProps: FormRenderProps): Promise<void> => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field) {
      return;
    }

    if (shouldEndDueToFileDependency(field)) {
      return;
    }

    await handleDependencies(fieldName, value, formRenderProps);

    if (isDateComplete(value) && field.formFiller && field.usesExtDictionary) {
      const currentFormValues = buildCurrentFormValues(fieldName, value, formRenderProps, ctFields);
      const response = await callLoadFieldValuesApi(fieldName, currentFormValues);
      fillFormFieldsFromResponse(response, formRenderProps);
    }
  };

  const onBoolFieldChange = async (fieldName: string, value: any, formRenderProps: FormRenderProps): Promise<void> => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field) {
      return;
    }

    if (shouldEndDueToFileDependency(field)) {
      return;
    }

    if (field.formFiller && field.usesExtDictionary) {
      const currentFormValues = buildCurrentFormValues(fieldName, value, formRenderProps, ctFields);
      const response = await callLoadFieldValuesApi(fieldName, currentFormValues);
      fillFormFieldsFromResponse(response, formRenderProps);
    }
  };

  const onChoiceFilterChange = async (
    fieldName: string,
    value: any,
    formRenderProps: FormRenderProps
  ): Promise<void> => {
    const field = ctFields.find(f => f.name === fieldName);
    if (!field || !field.searchable || !field.usesExtDictionary) {
      return;
    }

    fieldState.clearSearchTimer(fieldName);

    if (isValueEmpty(value)) {
      fieldState.clearFieldChoices(fieldName);
      return;
    }

    const currentFormValues = buildCurrentFormValues(fieldName, null, formRenderProps, ctFields);

    const timer = setTimeout(async () => {
      const elementId = fieldName;
      const response = await callLoadFieldValuesApi(fieldName, currentFormValues, value.toString());

      if (response?.values && response.values.length > 0) {
        fieldState.setFieldChoices(fieldName, response.values);
      } else {
        fieldState.clearFieldChoices(fieldName);
      }

      fieldState.clearSearchTimer(fieldName);

      requestAnimationFrame(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.focus();
        }
      });
    }, SEARCHABLE_FIELD_DEFAULT_DEBOUNCE_TIMER);

    fieldState.setSearchTimer(fieldName, timer);
  };

  const onChoiceSelectionChange = async (
    fieldName: string,
    value: any,
    formRenderProps: FormRenderProps
  ): Promise<void> => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field) {
      return;
    }

    if (shouldEndDueToFileDependency(field)) {
      return;
    }

    await handleDependencies(fieldName, value, formRenderProps);

    if (field.formFiller && field.usesExtDictionary && !isValueEmpty(value)) {
      const currentFormValues = buildCurrentFormValues(fieldName, value, formRenderProps, ctFields);
      const response = await callLoadFieldValuesApi(fieldName, currentFormValues);
      fillFormFieldsFromResponse(response, formRenderProps);
    }
  };

  const handleFileFieldChange = async (fieldName: string, files: FileList, formRenderProps: FormRenderProps) => {
    if (!files || files.length === 0) {
      return;
    }

    if (!contentType?.usesFilesProcessor || !selectedCtName) {
      return;
    }

    fieldState.addLoadingField(fieldName);

    try {
      const currentValues = buildCurrentFormValues(fieldName, null, formRenderProps, ctFields);

      const request: LoadFieldValuesFromFileRequest = {
        data: {
          contentTypeName: selectedCtName,
          metadata: currentValues
        },
        fileList: Array.from(files)
      };

      const response = await loadFieldValuesFromFile(request);

      if (!response?.valuesForOtherFields) {
        return;
      }

      const perFileFields = ctFields.filter(f => f.fileDependent === true);
      const hasPerFileFields = perFileFields.length > 0;

      if (hasPerFileFields) {
        const perFileFieldNames = new Set(perFileFields.map(f => f.name));
        const { perFileMetadata, sharedMetadata } = separateFileMetadata(
          response.valuesForOtherFields,
          perFileFieldNames
        );

        const currentFiles = formRenderProps.valueGetter(fieldName) || [];
        const updatedFiles = updateFilesWithPerFileMetadata(currentFiles, perFileMetadata);

        formRenderProps.onChange(fieldName, { value: updatedFiles });
        fillFormFieldsFromResponse({ valuesForOtherFields: sharedMetadata }, formRenderProps);
      } else {
        fillFormFieldsFromResponse(response, formRenderProps);
      }
    } catch {
      // empty
    } finally {
      fieldState.removeLoadingField(fieldName);
    }
  };

  const onManualSearch = async (fieldName: string, value: any, formRenderProps: FormRenderProps): Promise<void> => {
    const field = ctFields.find(f => f.name === fieldName);
    if (!field || !field.usesExtDictionary) {
      return;
    }

    if (isValueEmpty(value)) {
      return;
    }

    const currentFormValues = buildCurrentFormValues(fieldName, value, formRenderProps, ctFields);
    const response = await callLoadFieldValuesApi(fieldName, currentFormValues);

    fillFormFieldsFromResponse(response, formRenderProps);
  };

  const getManualSearchHandler = (field: ContentTypeFieldResponse): FieldChangeHandler => {
    if (field.searchable && field.formFiller && field.usesExtDictionary) {
      return onManualSearch;
    }
    return async () => {};
  };

  const getFieldChangeHandler = (field: ContentTypeFieldResponse): FieldChangeHandler => {
    switch (field.type) {
      case GEAR_CONTENT_TYPE_FIELD_TYPE.UNDEFINED:
      case GEAR_CONTENT_TYPE_FIELD_TYPE.CONTENT_TYPE:
      case GEAR_CONTENT_TYPE_FIELD_TYPE.CONTENT_TYPE_VERSION:
        return onUndefinedFieldChange;

      case GEAR_CONTENT_TYPE_FIELD_TYPE.STRING:
      case GEAR_CONTENT_TYPE_FIELD_TYPE.INT:
      case GEAR_CONTENT_TYPE_FIELD_TYPE.INT64:
      case GEAR_CONTENT_TYPE_FIELD_TYPE.DECIMAL:
        return onStringNumberFieldChange;

      case GEAR_CONTENT_TYPE_FIELD_TYPE.DATE:
      case GEAR_CONTENT_TYPE_FIELD_TYPE.DATETIME:
        return onDateFieldChange;

      case GEAR_CONTENT_TYPE_FIELD_TYPE.BOOL:
        return onBoolFieldChange;

      case GEAR_CONTENT_TYPE_FIELD_TYPE.CHOICE:
      case GEAR_CONTENT_TYPE_FIELD_TYPE.MULTI_CHOICE:
        return onChoiceSelectionChange;

      default:
        return onUndefinedFieldChange;
    }
  };

  const getChoiceFilterHandler = (field: ContentTypeFieldResponse): FieldChangeHandler => {
    if (field.searchable && field.usesExtDictionary) {
      return onChoiceFilterChange;
    }
    return async () => {};
  };

  const getFieldChoices = (field: ContentTypeFieldResponse): string[] => {
    if (field.usesExtDictionary && fieldState.dynamicChoices[field.name]) {
      return fieldState.dynamicChoices[field.name];
    }
    return field.choices || [];
  };

  const isFieldEnabled = (field: ContentTypeFieldResponse): boolean => {
    if (!field.dependsOnFields || field.dependsOnFields.length === 0) {
      return true;
    }

    return fieldState.enabledFields.has(field.name);
  };

  return {
    getFieldChangeHandler,
    getChoiceFilterHandler,
    getManualSearchHandler,
    loadingFields: fieldState.loadingFields,
    dynamicChoices: fieldState.dynamicChoices,
    getFieldChoices,
    handleFileFieldChange,
    isFieldEnabled
  };
};
